import React, { useState } from 'react';
import { Rocket, Sparkles, Bug, Zap, Shield, ChevronDown, ChevronRight, ArrowLeft, ShieldCheck, Brain, FileText, Mic, Users, Eye, Mail, Chrome, GitBranch, Copy, Send, Tag, Save, Filter, MessageSquareQuote, Calendar, Workflow } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RELEASES = [
    {
        version: 'v2.25.11',
        date: 'April 30, 2026',
        title: 'Email Template Insert — Visual Editor Now Usable',
        summary: 'Direct follow-up to yesterday\'s template Insert fix. Power user Peleg reported that templates inserted correctly but the visual block editor opened to an empty canvas, blocking per-email tweaks. Now an inserted template is wrapped in an editable text block + signature so the visual editor renders content you can drag, edit, and add blocks around.',
        categories: [
            {
                label: 'Visual Editor After Insert (FIX)',
                icon: FileText,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Inserting a template into a campaign email step now populates both the HTML body AND the visual block editor — open Edit Email and you\'ll see the template inside an editable text block with a signature block below',
                    'Drag, edit, or add additional blocks around the inserted template in the visual composer — exactly what Peleg asked for',
                    'On Save, the modal serialises the blocks into a full deliverable email document (proper DOCTYPE, MSO conditionals for Outlook, mobile-responsive scaffolding) — strictly an upgrade for deliverability vs. the raw template snippet',
                    'HTML mode still works as before — toggle in the editor to see/edit raw HTML',
                ],
            },
        ],
    },
    {
        version: 'v2.25.10',
        date: 'April 29, 2026',
        title: 'Multi-Word Search for Contacts & Companies',
        summary: 'Bug reported by Peleg: searching "ryan z" in Contacts returned 0 results, even though Ryan Zafris was right there. The search was matching the whole string as one regex against single fields — "ryan z" never appeared as a substring of any single field. Fixed for both Contacts and Companies.',
        categories: [
            {
                label: 'Tokenized Search (FIX)',
                icon: Filter,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Contact search now tokenizes on whitespace — "ryan z" splits to ["ryan", "z"] and each token must match somewhere among first name, last name, email, or title. "Ryan Zafris" now appears',
                    'Reverse order works too: "zafris ryan" → token "zafris" hits last_name, "ryan" hits first_name → ✓',
                    'Cross-field tokens work: "smith manager" finds a Smith with "manager" in their title',
                    'Same fix applied to Companies search — "Acme 2024" now matches a company named "Acme Corp 2024 LLC"',
                    'Empty search and single-word search behave identically to before — no regression',
                ],
            },
        ],
    },
    {
        version: 'v2.25.9',
        date: 'April 29, 2026',
        title: 'AI Chat Campaign Builder — Restored',
        summary: 'Bug reported by Matt: the AI Chat in Campaign Builder stopped building workflows and started outputting raw JSON in the chat bubble. Root cause: the endpoint was prompt-engineering JSON parsing (not real OpenAI tool use), and gpt-4o had drifted toward adding conversational preamble like "Sure! Here\'s your workflow:" before the JSON, which the parser couldn\'t handle.',
        categories: [
            {
                label: 'JSON Mode Forced (FIX)',
                icon: Brain,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Backend now passes response_format={"type": "json_object"} to OpenAI — guaranteed valid-JSON output, no preamble, no code fences, no markdown',
                    'Temperature dropped 0.7 → 0.3 — structured output benefits from lower creativity',
                    'New defensive parser handles every response shape we\'ve seen: pure JSON, markdown-fenced JSON, preamble + fence, preamble + raw JSON, and falls back to a regex-extracted outermost {…} block',
                    'When parsing fails completely, a WARN log captures the raw output to CloudWatch so future regressions are visible immediately instead of silently degrading UX',
                    '7/7 unit cases pass against realistic LLM output shapes',
                ],
            },
        ],
    },
    {
        version: 'v2.0.36',
        date: 'April 29, 2026',
        title: 'Re-Analyze Audio + Template Insert Fix',
        summary: 'Two ships in one release. Meeting Copilot gains a "Re-analyze audio" action for the case where a third attendee was added post-meeting and the original AI diarization (with the wrong speaker count) merged voices. On the CRM side, the campaign template Insert flow no longer silently overwrites the inserted body with stale block-editor state.',
        categories: [
            {
                label: 'Re-Analyze Audio (NEW)',
                icon: Mic,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New "Re-analyze audio" button in the Who\'s Who panel — re-runs AssemblyAI batch diarization on the saved meeting audio with the current attendee count as the expected speaker count',
                    'Use case: a third voice on the call wasn\'t on the calendar invite, so the original diarization ran with N–1 speakers and merged the missing person\'s voice with another. Add the missing attendee, click Re-analyze, AAI re-runs with the correct count',
                    'Detached background task — the panel flips to a spinner state, polls every 5s for up to 3 minutes, then auto-refreshes when complete with fresh AI suggestions',
                    'Honest expectation-setting in the confirm dialog: when two people were on the same Meet endpoint sharing one outbound audio channel, AAI may still merge them — same-channel diarization is genuinely hard. Worst case you spend a few cents to confirm AAI can\'t separate them, then manually retag in the transcript view',
                ],
            },
            {
                label: 'Template Insert Body Preserved (FIX)',
                icon: FileText,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Inserting a template into a campaign email step no longer silently reverts to old content on save',
                    'Root cause: stale email_blocks from prior visual-composer editing caused EmailEditorModal\'s Save to re-serialize the OLD blocks back over the freshly-inserted template body',
                    'Insert now also clears email_blocks + email_settings so the modal opens cleanly and preserves the template content (followed up the next day with v2.25.11 which fully populates the visual editor too)',
                ],
            },
        ],
    },
    {
        version: 'v2.0.35',
        date: 'April 29, 2026',
        title: 'Post-Meeting Who\'s Who — Real Rep Name',
        summary: 'Small polish ask from Matt — the speaker dropdown and tag popover used a generic "Sales Rep (you)" label everywhere. Now they show the rep\'s actual full name (Matt Kesby, etc.) pulled from the user profile.',
        categories: [
            {
                label: 'Rep Name Everywhere (POLISH)',
                icon: Users,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Speaker dropdown: "Sales Rep (you)" → "Matt Kesby (you)"',
                    'Click-to-tag popover: same — your real name with a (you) suffix',
                    'Talk-time bar legend: "Sales Rep 47%" → "Matt Kesby 47%"',
                    'Tagged transcript block labels: "Sales Rep" → "Matt Kesby"',
                    'Saved speaker_assignment contact_name fallback: same',
                    'Falls back to "Sales Rep" if the /me call fails (offline / token expired) — nothing breaks',
                ],
            },
        ],
    },
    {
        version: 'v2.0.34',
        date: 'April 29, 2026',
        title: 'Who\'s Who — Otter-Style Tagged Transcript',
        summary: 'Major UX upgrade for the post-meeting speaker resolver. Below the existing speaker cards (which stay as a compact "who\'s at the table" summary), there\'s now a full transcript view with block-grouped speaker turns and click-to-tag — modelled on Otter.ai and Descript. Matt: "the dropdown is still not showing enough, I think a new UI is needed where more of the transcription is shown and the user can tag sections of the transcript with the speaker."',
        categories: [
            {
                label: 'Tagged Transcript (NEW)',
                icon: FileText,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Consecutive same-speaker segments collapse into one paragraph block (Otter-style) — a 500-segment meeting → ~50–100 readable paragraphs instead of fragmentary one-line utterances',
                    'Each block: clickable speaker pill at the top with the resolved name + M:SS timestamp + full text with a left-edge accent in the speaker\'s stable color',
                    'Speaker pill renders the resolved name ("Matt Kesby" / "Ryan Zafris") not the raw "Speaker A" — so you see your assignments take effect across every utterance as you tag',
                    'Sticky header with a "Click any speaker label to re-tag" hint',
                    'max-height 480px with scroll-slim styling — keeps the page navigable on long meetings',
                ],
            },
            {
                label: 'Click-to-Tag Popover (NEW)',
                icon: Tag,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Click any speaker label → anchored popover opens',
                    'AI-suggested contact pinned at the top with a violet "AI suggested" badge (when one exists)',
                    'Sales Rep (you) row, every session attendee with their avatar, and "— Unknown / unassigned —" option',
                    'Free-text "Search contacts…" input filters the contact list as you type — handles 20+ attendee meetings without scrolling',
                    'No matches? An inline link prompts you to add the new attendee via the Attendees section (which you can scroll up to in the same view)',
                    'Closes on Escape, outside-click, or X button',
                    'Auto-clamps to the viewport on narrow windows so it never runs off the right edge',
                ],
            },
            {
                label: 'Talk-Time Bar (NEW)',
                icon: Eye,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'Stacked horizontal bar showing each speaker\'s share of total airtime',
                    'Stable per-speaker color (8-color palette) used as both the bar fill AND the transcript block accent — visual continuity across the whole panel',
                    'Legend below shows resolved name + percentage: "Matt Kesby 47% · Ryan Zafris 53%"',
                    'Quick visual signal: balanced split usually means the rep, dominant share usually means the lead voice on the prospect side',
                ],
            },
        ],
    },
    {
        version: 'v2.0.33',
        date: 'April 29, 2026',
        title: 'Who\'s Who — Expandable Per-Speaker Transcript',
        summary: 'First step in addressing Matt\'s ask: "we are going to need to see more of the transcript to make the allocations." Each speaker card now has an expandable list of every utterance attributed to that speaker, with timestamps. Plus clearer copy when AAI separated fewer voices than there are attendees on the meeting.',
        categories: [
            {
                label: 'Show All N Utterances (NEW)',
                icon: Eye,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Each Speaker A/B/C card has a "Show all N utterances by Speaker A →" toggle below the preview line',
                    'Expanded view: scrollable list of every utterance attributed to that speaker, with M:SS timestamps anchored to meeting start',
                    'Critical when AAI\'s single sample utterance is too generic to identify someone (e.g. Speaker B saying "Okay, good." once)',
                    'Cross-reference with your memory of the conversation, the recording, or the meeting agenda to identify the right person',
                ],
            },
            {
                label: 'Voice-vs-Attendee Count Copy (FIX)',
                icon: Mic,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Old: "AssemblyAI separated the audio into N speakers" — reads like the count is correct',
                    'New: "AssemblyAI detected N distinct voice signatures in the audio. You have M attendees but only N voices were separable — someone may not have spoken enough for AAI to register, or two voices were similar enough to be merged. The dropdowns still let you pick any attendee."',
                    'Sets accurate expectations when 3 attendees show only 2 speaker boxes — a real scenario when two people are on the same Google Meet endpoint sharing one outbound audio channel',
                ],
            },
        ],
    },
    {
        version: 'v2.0.32',
        date: 'April 29, 2026',
        title: 'Post-Meeting Attendees Section + Add Modal',
        summary: 'Matt: "Add a section for Attendees / Add the ability to add extra attendees in this post session view... There can be more attendees on the call that we need to tag." Editable list of who was on the call, with search-or-create flow for cold attendees discovered during the conversation.',
        categories: [
            {
                label: 'Attendees Section (NEW)',
                icon: Users,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New section in the post-meeting view between the Insights row and Action Items, showing every contact on the meeting',
                    'Each card: avatar, name, title, DISC badge, role tag, and the AI-derived insight summary inline',
                    'Hover any card → Remove button appears top-right (the AI-derived "Attendee insights" block lower in the page is removed — its content lives inside this section now)',
                    'Source of truth = session.contact_ids; the WhoIsWhoPanel speaker dropdowns and the AI re-suggest pass both pick up changes immediately',
                ],
            },
            {
                label: 'Add Attendee Modal — Dual Mode (NEW)',
                icon: Sparkles,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Search existing (default): debounced typeahead against the contacts API. Already-attached contacts render as muted "Added" rows so you can\'t double-add',
                    'Create new (toggle): minimal form (first/last/email/title) → POSTs to contacts API and adds them to the session in one flow. The empty-search state has an inline link "Create \'X\' instead?" that flips to create-mode pre-populated with the typed query',
                    'After Add: the Attendees section re-renders with the new card, the Who\'s Who dropdowns gain the new contact, and the next Re-suggest call sees the expanded contact list',
                ],
            },
        ],
    },
    {
        version: 'v2.0.31',
        date: 'April 28, 2026',
        title: 'VB-09-02 Role Play Live — 3-Column Redesign + VB-10 Debrief Polish',
        summary: 'Major design rebuild of the live Role Play session. Replaces the prior 2-column layout with the full 3-column shell from the design canon — Live Transcript · Playbook · AI Coach — matching the just-shipped VB-04a Live Meeting layout. The AI Coach runs the same analyser pipeline as a real meeting (BuyingSignal gauge + JIT cards), so reps now get real-time coaching during practice sessions. Plus polish on the post-session Debrief.',
        categories: [
            {
                label: 'Role Play Live — 3-Column Shell (NEW)',
                icon: Mic,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Live Transcript column with a floating AI Avatars overlay (top-left, dark glass, "AI AVATARS · LIVE" header, per-avatar speaking indicator)',
                    'Playbook column (340px) — stage tabs with activated/total count, active-question card with Deactivate, click-to-activate question list scoped to the selected stage',
                    'AI Coach column (320px) — BuyingSignalGauge (live) + JIT card feed (Follow-up Question / Product Match / Objection Risk) with local Use/Dismiss state',
                    'Top bar: N/M asked counter + Pause/Resume button',
                    'Audio pipeline preserved verbatim — the v2.0.30 250ms chunk override stays intact',
                ],
            },
            {
                label: 'Role Play AI Coach — Real-Time Coaching (NEW)',
                icon: Brain,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Backend RolePlayWebSocketManager now mirrors MeetingWebSocketManager — runs the same periodic analyser every 30s while a session is live',
                    'BuyingSignal gauge: programmatic talk-time score against a DISC-derived ideal band. Uses the first avatar\'s DISC primary as the "primary attendee"',
                    'Objection-risk detection (gpt-4o-mini, 30s cadence) on the last ~60s of transcript',
                    'Live product-recommendation detection on every rep transcript',
                    'Session loads the playbook on connect (stages + questions emitted via session_context), so the Playbook column has real content instead of a stub',
                ],
            },
            {
                label: 'VB-10 Debrief Polish',
                icon: Sparkles,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    '"+N vs last" delta pill on the Overall Score hero — computed from the rep\'s previous completed session. Red when down, green when up, hidden on session #1',
                    'Real TrendChart mini-line replacing the static placeholder — driven by the rep\'s last 30 sessions',
                    'Trend caption: "N sessions · Avg X → Y"',
                    'Top-edge accent bar on every PillarCard (full-width, score% filled green/amber/red)',
                ],
            },
            {
                label: 'Shared Component Extraction',
                icon: Workflow,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'BuyingSignalGauge + JITCoachCard pulled out of LayoutThreeColumn.jsx into desktop/src/components/livemeeting/ so both VB-04a Live Meeting and VB-09-02 Role Play share one source of truth — no drift between the two coaching surfaces',
                ],
            },
        ],
    },
    {
        version: 'v2.0.30',
        date: 'April 28, 2026',
        title: 'Role Play Latency — 4–8s Round-Trip Down to 1.5–2.5s',
        summary: 'Three-layer latency cut on Role Play. Matt: "the response times, the latency is too long." Diagnosis showed the avatar reply round-trip averaged 4–8 seconds, far slower than a phone call. After the fix, typical round-trip is 1.5–2.5 seconds — the difference between "robotic" and "feels like a real call."',
        categories: [
            {
                label: 'Mic Chunk Flush 5s → 250ms (FIX)',
                icon: Mic,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'The audio capture hook flushed mic chunks every 5 seconds — fine for the Meeting Copilot (bulk transcription, no fast turn-taking) but devastating for Role Play. When the rep stopped speaking, their last words sat in the buffer for up to 5s before being sent to AssemblyAI',
                    'New chunkIntervalMs option on useAudioCapture (default 5000 — meetings unchanged). Role Play now passes 250 — AAI sees the rep\'s last words within ~125ms on average',
                    'Saves ~2–4 seconds typical round-trip',
                ],
            },
            {
                label: 'LLM Streaming + First-Sentence TTS (FIX)',
                icon: Zap,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'The orchestrator awaited the full LLM reply before opening TTS. Now it streams the gpt-4o-mini completion and fires Text-to-Speech on the first complete sentence as soon as it arrives — the rep hears the avatar speaking ~700ms after the first sentence is ready, instead of waiting for the entire reply to generate',
                    'Sentence boundary heuristic: any of .!? followed by whitespace, after >=12 chars of "real" content. Short prefixes like "Mr." batch with the next sentence to avoid splitting on abbreviations',
                    'Trailing buffer at end-of-stream is emitted as the final segment for single-sentence replies',
                    'Saves ~400–700ms perceived latency',
                ],
            },
            {
                label: 'Singleton OpenAI Client + In-Mem Cache (FIX)',
                icon: Workflow,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'Module-level AsyncOpenAI singleton in role_play_orchestrator.py — keeps the httpx connection pool warm across calls. Previously a fresh client was created per turn, throwing away the pool every time',
                    'New in-memory rolling window of recent transcript segments on the WS manager — the orchestrator reads from RAM instead of Mongo, saving a database round-trip per turn',
                    'Concurrent Mongo write + avatar reply trigger (was sequential) — uses the in-memory cache for orchestrator context so the persist task and the orchestrator can run in parallel',
                    'Saves ~150–250ms per turn',
                ],
            },
        ],
    },
    {
        version: 'v2.0.29',
        date: 'April 28, 2026',
        title: 'Speaker Resolver Phase 2 + ViewBadge Default-Off',
        summary: 'Phase 2 of the multi-attendee speaker resolver — confirmed names from the rep-reviewed "Who said what?" panel are now threaded through post-meeting summary regeneration. Plus the VB-XX design badges that have been visible in production since the design canon rebuild are now hidden by default — only design reviews see them.',
        categories: [
            {
                label: 'Confirmed Names in Post-Meeting Summary (NEW)',
                icon: Brain,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New _build_labeled_transcript helper re-threads speaker names back into a line-per-utterance transcript using the confirmed speaker_assignments[] from the Who\'s Who panel',
                    'Falls back to "Sales Rep" / "Speaker B" defaults when the rep hasn\'t confirmed assignments yet',
                    'process_post_meeting now feeds this labeled transcript to the AI instead of the raw concatenated full_transcript (which AAI batch returns with no speaker labels at all)',
                    'Net effect: post-meeting summaries, action items, and attendee_sentiment all get real names threaded throughout — "Neelika Patel led the conversation…" instead of "Speaker A led…"',
                    'Rep regenerates the summary by clicking the existing Process button after confirming assignments — no auto-regen on assignment save (keeps AI cost predictable, lets the rep batch their corrections)',
                ],
            },
            {
                label: 'VB-XX Badges Hidden by Default (POLISH)',
                icon: Eye,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'The purple "VB-XX" badges in the top-right of every desktop window have been visible in production since the design canon rebuild — useful during reviews, but customer-facing noise',
                    'Default flipped to HIDDEN. Re-enable for design reviews via REACT_APP_SHOW_VIEW_BADGES=true at build time, or localStorage.setItem("showViewBadges", "1") at runtime',
                    'Legacy hideViewBadges="1" runtime flag still honored',
                ],
            },
        ],
    },
    {
        version: 'v2.0.28',
        date: 'April 28, 2026',
        title: 'Multi-Attendee Speaker Resolver',
        summary: 'Closes the long-standing "true same-room speaker diarization" item from the desktop app to-do list. After a meeting, AssemblyAI now diarizes the saved audio (separate voices into A, B, C labels), the AI auto-suggests which CRM contact each speaker is, and a new "Who said what?" panel in the post-meeting view lets the rep confirm or override each match.',
        categories: [
            {
                label: 'Post-Meeting Diarization (NEW)',
                icon: Mic,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Mic audio is buffered during the live session and uploaded to S3 on session end',
                    'After upload, AssemblyAI batch transcription with speaker_labels=true partitions the audio into distinct voices (A, B, C, …)',
                    'Diarized utterances replace the streaming-era transcript_segments on the session — full meeting now has speaker labels',
                ],
            },
            {
                label: 'AI Auto-Suggest (NEW)',
                icon: Brain,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'New speaker_resolver service runs after diarization completes — uses gpt-4o-mini to match each anonymous A/B/C label to a CRM contact based on what each speaker said',
                    'Prompt context: every meeting attendee\'s name, title, role, DISC profile, plus 5 sample utterances per speaker',
                    'Returns a strict-JSON match list with confidence (high / medium / low) + reasoning per speaker',
                    'Cost: ~$0.005/meeting on top of the ~$0.006/min AAI batch — negligible',
                ],
            },
            {
                label: 'Who Said What? Panel (NEW)',
                icon: Users,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'New section in the post-meeting view: search-card per speaker with the AI confidence badge, a sample utterance, the AI\'s reasoning, and a dropdown of meeting attendees + Sales Rep + Unknown',
                    'Confirm or override each suggestion, then click Save to persist the speaker → contact mapping back to the session',
                    'Re-suggest button runs the AI over fresh, in case attendees changed or the rep wants a second pass',
                    'Auto-hides on 1-speaker meetings or sessions where assignments are already all rep-tagged — no clutter when there\'s nothing to resolve',
                ],
            },
        ],
    },
    {
        version: 'v2.24.0',
        date: 'April 25, 2026',
        title: 'Campaign Builder Pro Tools — Templates, Duplication, Connector Editing & Engine Reliability',
        summary: 'Massive Campaign Builder upgrade driven by power-user feedback from We Conquer Media. Twenty fixes and features shipped in a single day, including a new email templates sidebar, drag-to-edit connectors, full step duplication, an unsaved-changes guard, fixes to the sequence engine itself (wait steps, branching, tag triggers), bulk actions on the Contacts page, and explicit inclusive date operators for conditions. If you build campaigns in NurtureCRM, today is the day everything got faster.',
        categories: [
            {
                label: 'Email Templates Sidebar (NEW)',
                icon: FileText,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New "Templates" button in the campaign builder topbar — opens a 320px sidebar with full template management',
                    'Insert any template into the currently-selected email step in one click — subject + body load instantly',
                    '"Save current email as template" quick-action — pre-fills the form with whatever you\'ve composed in the selected step (name, subject, body) so you can capture great copy without re-typing',
                    '"+ New blank template" button for creating from scratch',
                    'Search bar + scope filter (All / Mine / Org) — find any template by name, subject, or category',
                    'Per-template Insert / Edit / Delete actions inline on each card',
                    '"Share with org" toggle for admins — instantly promote a personal template to a shared org template',
                    'Templates saved from the Campaign Builder are flagged campaign-only — they don\'t pollute the sales-side template picker (Inbox, Meeting actions, Contact composer)',
                    'Reuses the existing email template infrastructure — admin templates, personal templates, and org scoping all work as before, just with the new sidebar surface',
                ],
            },
            {
                label: 'Step Duplication (NEW)',
                icon: Copy,
                color: 'text-indigo-500 bg-indigo-50',
                items: [
                    'Hover any step on the canvas — a small Copy icon appears top-left',
                    'Click to duplicate — the step clones beside the original with the same step type, same configuration (email body, merge variables, conditions, wait durations, all of it), and a "(copy)" suffix on the label so you can tell them apart',
                    'New step gets a fresh step ID and an offset position so it doesn\'t overlap the original',
                    'Wire the new step\'s connections however you want — duplication does not copy the source step\'s edges, giving you full control over routing',
                    'Toast confirmation shows which step was duplicated',
                ],
            },
            {
                label: 'Edit Connectors (NEW)',
                icon: GitBranch,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Edges between steps are now fully editable — hover any edge and the endpoints become draggable handles',
                    'Drag either endpoint (source or target) to a different node and the edge re-attaches in place',
                    'Same edge identity is preserved — only the source/target change, so labels and conditions on the edge survive the move',
                    'Wider grab zone (20px) makes endpoints easier to find and grab without misclicking the canvas',
                    'Drop on empty canvas to cancel — the edge stays where it was',
                ],
            },
            {
                label: 'Unsaved-Changes Protection (NEW)',
                icon: Shield,
                color: 'text-red-500 bg-red-50',
                items: [
                    'Amber "Unsaved changes" pill next to the Save button + asterisk on the button label whenever you have unsaved work',
                    'Browser refresh, tab close, or external navigation triggers a confirmation prompt when there are unsaved changes',
                    'Back arrow + Execution Report button now also confirm before navigating away if there are unsaved changes',
                    'Save error messages now show the actual server error detail in the toast (was previously a generic "Failed to save") — much easier to diagnose what went wrong',
                ],
            },
            {
                label: 'Branch & If/Else Behaviour (FIXES)',
                icon: GitBranch,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'If/Else and Branch nodes now show two clearly-labeled output handles at the bottom — green "Yes" / grey "No" for If/Else, "A" / "B" for AB Split — so you can wire each branch correctly without guessing which output is which',
                    'Branch step now actually filters when used as the first action — contacts whose condition fails are excluded from the campaign instead of being passed through silently',
                    'If/Else routing is functional again — your "Yes" path goes to the first wired step, "No" path goes to the second, exactly as you\'d expect',
                    'Multi-output routing pattern (one step fanning out to multiple branch nodes) preserved — each branch\'s condition is evaluated and the matching path is taken',
                ],
            },
            {
                label: 'Wait & Delay Step Reliability (FIXES)',
                icon: Calendar,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Wait steps with non-zero durations now correctly advance contacts when the wait elapses — previously a contact could get stuck in a Wait step indefinitely (some enrollments were stuck for over two weeks)',
                    'Mid-flight duration edits are now honored — if you shorten a Wait from 2 days to 1 hour, contacts who entered before the change pick up the new shorter duration on their next scheduler check',
                    'Existing stuck enrollments auto-recover within 24 hours of the deploy as their currently-scheduled timer rolls past',
                    'Tag-triggered campaigns now fire only when the configured tag is added — previously they fired on ANY new tag, routing wrong contacts into the wrong sequences',
                    'Wait → Condition step ordering now works correctly — the delay actually happens before the condition is evaluated',
                ],
            },
            {
                label: 'Inclusive Date Operators (NEW)',
                icon: Calendar,
                color: 'text-teal-500 bg-teal-50',
                items: [
                    'Two new date operators in conditions: "is on or before date" and "is on or after date" — boundary day is included',
                    'Existing "is before date" and "is after date" remain strict (boundary day excluded) and have been relabeled "is strictly before/after date" for clarity',
                    'No migration needed — existing campaigns keep their current operators; switch when you want',
                ],
            },
            {
                label: 'Contacts Page Bulk Actions (FIXES + NEW)',
                icon: Users,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Bulk Tag now actually applies — fix for a permission-name mismatch that was silently failing for all users',
                    'New "Enroll" button in the bulk-action bar — select multiple contacts, pick a campaign from the dropdown (status + step count shown for each), and enrol them all in one click',
                    'Skipped enrollments (already-enrolled contacts, missing data) are surfaced in the result toast so you know what happened',
                ],
            },
            {
                label: 'Step Insights (FIX)',
                icon: Eye,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Contact count badges on every step are now always visible — small numbers in the top-right corner of each node show how many contacts are currently AT that step (blue) and how many have PASSED THROUGH (stone)',
                    'Click either badge to open the contact list panel — see exactly who\'s where in your sequence, with entry timestamps and outcomes',
                    'Previously the badges only appeared when there was traffic, making the click target invisible on freshly-built sequences',
                ],
            },
            {
                label: 'Campaign Duplication (FIX)',
                icon: Copy,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Duplicating a campaign now correctly copies all of its steps and edges — previously the duplicate would show the right step count in the preview but render an empty canvas',
                    'All duplicated steps retain their full configuration (email body, conditions, wait times, branch logic, etc.)',
                    'Edges are remapped to the duplicated step IDs so the workflow logic is intact in the new copy',
                    'Forms referenced by triggers are also duplicated automatically and re-linked',
                ],
            },
            {
                label: 'Email Image Truncation (FIX)',
                icon: Mail,
                color: 'text-green-500 bg-green-50',
                items: [
                    'Pasted/dropped images in campaign emails no longer cause Gmail to truncate the message',
                    'Outgoing emails are scanned server-side: any inline base64 image is uploaded to S3 and the <img> src is rewritten to a permanent URL — keeps the HTML payload small (well under Gmail\'s 102KB clipping threshold)',
                    'Works automatically for all outbound paths: campaign sends, manual Gmail composer, sequence emails',
                    'Existing emails will start displaying properly the next time they\'re re-sent',
                ],
            },
        ],
    },
    {
        version: 'v2.23.5',
        date: 'April 24, 2026',
        title: 'CAN-SPAM Compliance, Engine Reliability & Marketing Operations',
        summary: 'Critical compliance work (CAN-SPAM / GDPR unsubscribe), several reliability fixes for the campaign engine and signature rendering, and a wave of marketing-ops improvements: cleaner staging commits, accurate org counts, first-class Source Tag and Status Event fields on contacts, tracker improvements, and a new automation pathway via the GitHub + Claude Code integration.',
        categories: [
            {
                label: 'Unsubscribe & CAN-SPAM/GDPR Compliance',
                icon: Shield,
                color: 'text-red-500 bg-red-50',
                items: [
                    'Every marketing email (campaign sends + Gmail composer sends) now includes a one-click unsubscribe link in the footer — required by CAN-SPAM and GDPR',
                    'List-Unsubscribe + List-Unsubscribe-Post headers attached for native Gmail/Yahoo "Unsubscribe" buttons (RFC 8058 one-click)',
                    'Public unsubscribe page at /unsubscribe?t=… with branded confirmation flow — recipient can opt out without signing in',
                    'HMAC-signed tokens — unsubscribe links are tamper-proof',
                    'Unsubscribed recipients automatically excluded from future marketing sends — campaign sends mark them as "skipped_unsubscribed" so the enrollment continues without sending',
                    'Manual Gmail composer blocks sending to unsubscribed recipients with a clear error',
                    'Transactional emails (password reset, invites, booking confirmations) intentionally NOT gated, per the CAN-SPAM transactional exemption',
                ],
            },
            {
                label: 'Campaign Engine Reliability',
                icon: Workflow,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Fixed a double-send race in the campaign scheduler — under load (with two backend workers running concurrently), the same email could be delivered twice. The claim is now an atomic find_one_and_update with optimistic locking on the original next_action_at value, so only one worker wins per tick',
                    'Applied the same fix to the activation campaign scheduler in the ATS module',
                    'Diagnostic script saved to backend/scripts/diag_campaign_duplicate_send.py for future audits',
                ],
            },
            {
                label: 'Email Signatures — Permanent Asset URLs',
                icon: Mail,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Profile photos and company logos in email signatures now render correctly for recipients opening the email any time after sending',
                    'New public proxy endpoints (/api/public/user-photo/{id}, /user-logo/{id}, /org-logo/{id}) 302-redirect to fresh S3 presigned URLs on every read — the link never expires while the underlying file exists',
                    'Previously, signature merge variables baked a 1-hour S3 presigned URL into the MIME body — emails opened more than an hour after send showed broken images',
                    'Old sent emails can\'t be retroactively healed (the URL is in the recipient\'s mailbox), but new outgoing emails are durable',
                    'Profile photo "blob URL leak" fixed — Settings → Profile would accidentally save a temporary browser blob: URL through the profile API, breaking the Header avatar and signature thumbnails. Three-layer defense now: login regenerates presigned URLs, backend strips blob:/data: URLs with a warning log, frontend destructures those fields out of the PUT body',
                ],
            },
            {
                label: 'Settings & Plan Limits — Soft-Delete Aware',
                icon: Filter,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Settings → Organization counts (Contacts, Companies, Deals) now exclude soft-deleted records — large deletions no longer inflate the displayed numbers',
                    'Plan-limit enforcement also excludes soft-deleted records — orgs that had cleared their data were incorrectly being treated as over-limit',
                    'Affects every "soft-deletable" collection (contacts, accounts, deals); other collections (users, sequences, deal rooms, campaigns, forms) keep their straight count',
                ],
            },
            {
                label: 'Staging Area & Import Wizard',
                icon: Users,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'Commit Preview now surfaces existing CRM contacts BEFORE you click Commit — no more "I committed and got duplicates" surprises. New tiles show New + Existing Companies/Contacts, an expandable list of matches with the matched_by reason (email or name+account), and per-rep new/existing splits',
                    'Staging Table picks up Tags, HQ Country, and State/Province as default columns — visible + filterable directly from the staging review screen',
                    'Tags column on CSV imports — per-contact tags now actually save (was silently dropping for weeks). Backfill applied: 83 affected contacts at AiCoaches re-populated from the Sydney + Doha leadership-dinner batches',
                    '"Update existing records" mode now fills in missing fields on matched contacts (email, phone, mobile, title, department, seniority, LinkedIn URL, role in deal, source tag, status event) — never overwrites existing values',
                    'Import Wizard fixes: Personal Phone and Company Phone no longer collide in the field picker; revenue parser handles em-dash/en-dash and "$500M+" formats; "Annual Revenue" reads the original range string in the UI',
                    'Source Tag and Status Event are now first-class contact fields — they save through the staging commit and appear in Contact Detail / Staging Table / Import Wizard consistently',
                ],
            },
            {
                label: 'Tracker, /inbox & Magic Dispatch',
                icon: MessageSquareQuote,
                color: 'text-indigo-500 bg-indigo-50',
                items: [
                    'New /inbox, /bugs, and /features slash commands for fast tracker review',
                    'Tracker → GitHub Issues sync — feature/bug submissions automatically create matching GitHub issues, status changes mirror to GitHub labels, and "released" status closes the GitHub issue',
                    'New "Dispatch to Claude Code" button on tracker cards — fires a Claude Code routine with full ticket context for autonomous triage',
                    'Bug report attachments now load on demand in the SuperAdmin tracker — clickable thumbnail grid per ticket',
                ],
            },
            {
                label: 'Contacts Page UX',
                icon: Tag,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'New tag filter on the Contacts page toolbar — narrow the table to contacts matching one or more tags',
                    'Tag management overhaul — admins can now create, rename, and manage tags from a dedicated Tags tab. New tags appear in pickers immediately even before any contact has them',
                    'Remove-from-sequence button on the Campaign Progress card — pull a contact out of an active enrollment with one click',
                    'Form Builder: map any field to a Company-level field (industry, size, city, country, etc.) — submission upserts the account and links the contact via account_id',
                    'New sequence nodes now place just below the currently-selected node instead of in a fixed grid that often pushed nodes out of viewport',
                ],
            },
        ],
    },
    {
        version: 'v2.23.3',
        date: 'April 22-23, 2026',
        title: 'AI Event Outreach Studio + Activities Inline Edit',
        summary: 'Phase 1 of the AI Event Outreach Studio — a dedicated workflow for warming up event attendees before the meeting and following up after, with AI-drafted outreach across multiple channels. Plus a major Activities upgrade with inline edit cards, an audit log, and merged contact feed; richer Events module with platform links and inline edit; and Prompt Forge for internal prompt iteration.',
        categories: [
            {
                label: 'AI Event Outreach Studio',
                icon: Sparkles,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New /events/{id}/outreach page — guided pre-event and post-event outreach for every attendee',
                    'AI drafts personalized messages per attendee using the contact\'s DISC profile, the event context, and the sender\'s voice',
                    'Per-message link inclusion matrix — control which links (Event Page, Connect Request, Custom CTA) appear in each message of the cadence',
                    'Voice-to-text dictation on the Context textarea — speak your additional context, AI captures and incorporates it',
                    '"Connect-first" pre-event cadence pattern — establish a relationship before the event without forcing a hard sale',
                    'Tracked Event Page links so you can see which attendees clicked through (deferred — link tracking infra dropped this round in favor of a simpler matrix; full tracking returns in v2.23.0\'s email tracking work)',
                ],
            },
            {
                label: 'Events Module Improvements',
                icon: Calendar,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Status dropdown on event detail — change between Draft / Published / Live / Completed inline',
                    'Platform selector — Zoom or Velocity (your white-labeled video platform), with platform-specific link fields',
                    'Inline Edit on event detail — no modal needed for quick fixes',
                    '"Add Contacts" picker now spans every published event\'s attendee pool, not just the current event',
                    'Fixed: EventDetail Edit button was opening a non-existent dashboard route',
                ],
            },
            {
                label: 'Activities — Inline Edit + Audit Log',
                icon: FileText,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Activity cards on Contact Detail are now inline-editable — click to edit, save in place, no modal',
                    'Full audit log per activity — every edit, status change, owner reassignment is recorded with timestamp + user',
                    'Merged feed — calls, emails, meetings, tasks all appear in a single chronological timeline on Contact Detail',
                    'Detail/edit modal for richer activity views with full history',
                    'Fixed "Activity not found" 404 errors when clicking through from older sources',
                ],
            },
            {
                label: 'Companies & Contacts',
                icon: Users,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'Companies table: "Created Date" column added to the default view',
                    'Org chart on Account Detail now surfaces all company contacts (was previously truncating)',
                    'Contact Detail terminology cleanup — "Sequences" tab renamed to "Campaigns" to match the rest of the product',
                ],
            },
            {
                label: 'Meeting Copilot — Attendee Editing on Live Sessions',
                icon: Mic,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Add or remove attendees on a meeting that\'s already scheduled or even live',
                    'Helpful when someone joins late, drops, or you realize you forgot to add the right CRM contact at scheduling time',
                ],
            },
            {
                label: 'Prompt Forge (Internal)',
                icon: Sparkles,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'Internal tool for iterating on AI prompts directly inside the app — derive prompt context from any page, capture screenshots, and ship to the tracker as a feature/bug',
                    'Bumped to 0.1.1 with real Web Speech API for voice prompts',
                    'Auto-derives page IDs at AppLayout so context is always populated',
                ],
            },
        ],
    },
    {
        version: 'v2.23.1',
        date: 'April 20-21, 2026',
        title: 'Meeting Copilot v2.0 (VB-XX Rebuild) + Practice Module + AssemblyAI Streaming',
        summary: 'A ground-up rebuild of the Meeting Copilot desktop app to a new design canon (VB-XX), shipping waves 1 and 2 in two days. New Practice module for sales-rep training with AI Avatar role plays, micro-practice drills, and a leaderboard. AssemblyAI Universal-Streaming replaces Whisper for live transcription — better quality, true speaker labels, and a whole new tier of pre-meeting AI briefings.',
        categories: [
            {
                label: 'VB-XX Design Canon — Wave 1 + Wave 2',
                icon: Sparkles,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'All Meeting Copilot screens redesigned against a single design system (VB-01 Home, VB-02 New Meeting, VB-03 Prep Brief 4-tab, VB-04 Live Meeting with two layout variants, VB-05 Mini Recording window, VB-06 Post-Meeting review, VB-07 Contact Deep-Dive, VB-08 Floating Overlay, VB-09 Role Play, VB-10 Debrief, VB-11 Leaderboard)',
                    'VB-01 Home — 4-stat header (today\'s coverage delta, drills completed, meetings logged, role plays), drill nudge for the day, dual CTAs for new meeting and role play',
                    'VB-02 New Meeting — AI prep nudge, DISC + role chips on attendees, win-rate playbook cards',
                    'VB-04a Live Meeting (3-column) — buying signal gauge, JIT coach cards (follow_up / product_match / objection_risk variants), header status pills (questions asked, connected, live)',
                    'VB-04b Live Meeting (coach-first) — alternative layout for reps who want the coach center stage',
                    'VB-05 Mini Recording — break-away always-on-top window for when you need the rest of your screen',
                    'VB-06 Post-Meeting — 3-tab Overview / Attendees & Recs / Email draft slide-over',
                    'VB-07 Contact Deep-Dive — break-away pop-out with full Recent Activity timeline (merged calls, emails, meetings, tasks)',
                    'VB-08 Floating Overlay — redesigned Questions / Coach / Notes panel with transparent body',
                    'Settings → Live Layout toggle — pick A (3-column) or B (coach-first)',
                ],
            },
            {
                label: 'AssemblyAI Universal-Streaming Live Transcription',
                icon: Mic,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Replaces Whisper batch as the default live transcription engine',
                    'Streaming WebSocket to AssemblyAI Universal (PCM 16kHz, 16-bit) — sub-second latency from speech to on-screen text',
                    'Turn-based speaker labels: mic channel (your voice) is "rep", system audio channel (remote participants) gets "attendee" or "other_X" labels',
                    'WAV-header stripping + silence gating before forwarding to AssemblyAI — cuts dead air and avoids 3007 disconnects',
                    'Whisper kept as fallback (set TRANSCRIPTION_PROVIDER=whisper or omit ASSEMBLYAI_API_KEY)',
                    'Cost-per-minute logged to ai_usage_logs for org-level billing visibility (~$0.0078/min)',
                    'Future post-meeting batch call with speaker_labels=true is planned for true same-room voice diarization',
                ],
            },
            {
                label: 'Practice Module + Role Play',
                icon: Brain,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'New Practice tab — drill against AI avatars to rehearse discovery calls, demos, objection-handling, negotiation',
                    'VB-09 Role Play Setup — pick a playbook (Change button to switch mid-flow), choose your avatar, set the scenario',
                    'VB-09-02 Role Play Live — real-time conversation with the avatar, full live transcription, on-the-fly coaching',
                    'VB-10 Debrief — score on multiple dimensions, transcript with highlighted moments, recommended next drill',
                    'VB-11 Leaderboard — team-level + individual stats, badges for streaks and depth',
                    'Daily micro-practice drill nudges from the Home dashboard — short focused reps to keep skills sharp',
                    'Avatar Library admin tab for managing the personas and scenarios available to your team',
                ],
            },
            {
                label: 'Live Meeting Capture Resilience',
                icon: ShieldCheck,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Mic-first ordering — system-audio capture no longer kills the microphone if it\'s offered later',
                    'In-meeting mic picker — switch input devices without ending the meeting',
                    'Pause/Resume recording (amber Pause + green Resume buttons) — meeting timer and audio capture both pause and resume cleanly, MediaRecorder native pause/resume used',
                    'Transparent body for VB-05 mini recording + VB-08 overlay windows — they sit on top of your work without blocking it',
                    'Required Chromium switches set in Electron main before app.whenReady() — autoplay-policy=no-user-gesture-required, disable-renderer-backgrounding, disable-background-timer-throttling, disable-backgrounding-occluded-windows — stops AudioContext from suspending mid-meeting',
                ],
            },
            {
                label: 'Pre-Meeting Brief Generator',
                icon: FileText,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'AI prep brief now emits structured opportunities, risks, and recommended plays per meeting — not just narrative text',
                    'VB-03 Prep tabs surface: Brief / Playbook / Company / People — each populated from the contact and account context files',
                    'Defensive None-handling on the prep generator endpoint — no more 500s when a contact is missing optional fields',
                    'Empty-state cards on VB-03 (Opportunities / Risks / Plays) hidden when there\'s no content rather than showing blank tiles',
                ],
            },
            {
                label: 'Desktop App Build & Release',
                icon: Zap,
                color: 'text-indigo-500 bg-indigo-50',
                items: [
                    'Desktop app v2.0.16 → v2.0.27 shipped over the rebuild waves',
                    'Signed + notarized macOS arm64 DMG/ZIP, unsigned Windows x64 NSIS installer (Authenticode cert pickup pending)',
                    'Auto-update wired through api.nurturecrm.ai/api/public/update — v2.0.22+ installs self-update on relaunch',
                    'Dev/Production parity — REACT_APP_API_URL honored in Electron main, dev port configurable via $PORT, role-play avatar audio allowed via blob: media-src',
                ],
            },
        ],
    },
    {
        version: 'v2.23.0',
        date: 'April 15, 2026',
        title: 'Email Tracking — HubSpot-Style Open, Click & Reply Tracking',
        summary: 'Full email engagement tracking for both the CRM Inbox and Gmail (via Chrome extension). Know exactly when prospects open your emails, click your links, and reply — with real-time desktop notifications the moment it happens.',
        categories: [
            {
                label: 'Open & Click Tracking',
                icon: Eye,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'Invisible tracking pixel automatically injected into outgoing emails — records every open with timestamp, location, and device info',
                    'All links in tracked emails are wrapped for click tracking — see which links each recipient clicks and how many times',
                    'New "Tracking" tab in the Inbox page — view all tracked emails at a glance with recipient, subject, status, open count, click count, and sent date',
                    '"Track opens & clicks" toggle in the compose dialog — enabled by default, one click to turn off for sensitive emails',
                    'Status badges show email lifecycle at a glance: Sent → Opened → Clicked → Replied',
                    'Per-email detail view with full event timeline showing every open, click, and reply with timestamps',
                ],
            },
            {
                label: 'Real-time Notifications',
                icon: Zap,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Instant toast notifications when a prospect opens your email, clicks a link, or replies — powered by Server-Sent Events (SSE)',
                    'Notifications show the contact name, action taken, and email subject so you can follow up at the perfect moment',
                    'Live data refresh — the Tracking tab updates automatically when new events arrive, no manual refresh needed',
                    'Campaign email events from Resend webhooks also trigger real-time notifications in the same stream',
                ],
            },
            {
                label: 'Reply Detection',
                icon: Mail,
                color: 'text-green-500 bg-green-50',
                items: [
                    'Automatic reply detection — the system monitors Gmail threads for new messages after your tracked send',
                    'Background polling every 60 seconds catches replies within a minute of them arriving',
                    'Replies update the email status to "Replied" and fire a real-time notification',
                    'Reply events are logged in the email timeline alongside opens and clicks for a complete engagement picture',
                ],
            },
            {
                label: 'Chrome Extension for Gmail',
                icon: Chrome,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'New NurtureCRM Email Tracker Chrome extension — track emails sent directly from Gmail\'s web interface',
                    '"Track" toggle injected next to Gmail\'s Send button — one click to enable tracking on any email you compose',
                    'Extension popup shows login status and recent tracking activity at a glance',
                    'Secure PKCE authentication — sign in once with your NurtureCRM account, stays connected',
                    'Tracking data from Gmail and the CRM Inbox all flows into the same Tracking tab for a unified view',
                ],
            },
            {
                label: 'Compliance & Deliverability',
                icon: Shield,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'One-click unsubscribe links in every tracked email — HMAC-verified for security, branded confirmation page',
                    'Suppression list automatically prevents sending to unsubscribed, bounced, or complained addresses',
                    'Per-contact tracking history accessible from the Contact Detail page',
                    'Tracking pixel uses no cookies, no cache headers, and minimal 43-byte payload for maximum deliverability',
                ],
            },
        ],
    },
    {
        version: 'v2.22.0',
        date: 'April 14, 2026',
        title: 'Meeting Copilot v1.2.0 — Contact Linking, Pause/Resume & Live Transcription',
        summary: 'Major update to the Meeting Copilot desktop app (v1.2.0 Beta). New meetings can now be linked to CRM contacts via search or quick-add, recordings can be paused and resumed mid-meeting, and live transcription is now functional with Deepgram real-time speech-to-text integration.',
        categories: [
            {
                label: 'Contact Search & Quick Add',
                icon: Users,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New "Meeting Attendees" section on the New Meeting page — search CRM contacts by name, email, or job title with debounced type-ahead dropdown',
                    'Selected contacts displayed as removable chips with name and company — linked to the meeting session via contact_ids',
                    '"New" button opens a Quick Add Contact form (first name, last name, email, phone, job title) that creates the contact in your CRM and immediately adds them to the meeting',
                    'Search results show contact name, job title, company name, and email for easy identification',
                    'If no contacts match, a "Create new contact" link pre-fills the form from the search query',
                    'Meeting notes, transcripts, and post-meeting summaries are now linked to the correct contact records in the CRM',
                ],
            },
            {
                label: 'Pause & Resume Recording',
                icon: Mic,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'New Pause button (amber) and Resume button (green) in the meeting header bar, next to End Meeting',
                    'Recording indicator changes from red pulsing "REC" to amber static "PAUSED" when paused',
                    'Meeting timer pauses while recording is paused — elapsed time only counts active recording',
                    'Audio capture (mic + system) pauses and resumes using MediaRecorder native pause/resume',
                    'Overlay window notified of pause/resume state',
                    'Transcript area shows "Paused" state when no audio is being captured',
                ],
            },
            {
                label: 'Live Transcription (Deepgram)',
                icon: Sparkles,
                color: 'text-emerald-500 bg-emerald-50',
                items: [
                    'Deepgram real-time streaming speech-to-text now fully operational — transcripts appear in the Live Transcript panel during meetings',
                    'Fixed critical audio encoding mismatch — browser sends WebM/Opus containers but Deepgram was configured for raw Opus frames; now uses auto-detection for correct container parsing',
                    'Deepgram API key added to backend environment and SSM Parameter Store for production deployments',
                    'Nova-2 model with speaker diarization — mic channel (rep) and system audio channel (remote participants) transcribed separately',
                    'Real-time answer detection triggers automatically from transcript buffer — playbook questions marked as answered with AI-generated summaries',
                ],
            },
            {
                label: 'Desktop App Build & Deploy',
                icon: Zap,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Desktop app v1.2.0 Beta — macOS (signed + notarized, Apple Silicon) and Windows (x64 NSIS installer)',
                    'Download page at nurturecrm.ai/desktop-app updated with v1.2.0 artifacts and BETA badge',
                    'Added DEEPGRAM_API_KEY to Terraform user_data.sh for future EC2 instance launches',
                    'Backend v2.11.1 deployed with Deepgram integration and audio encoding fixes',
                ],
            },
        ],
    },
    {
        version: 'v2.21.0',
        date: 'April 13, 2026',
        title: 'AI Product Content Writer, Signature Enhancements & Form Improvements',
        summary: 'Major upgrade to the Product module with an AI-powered content rewriting system that transforms raw product information into structured marketing messaging. Signature templates now resolve merge variables and render profile photos. Forms gain redirect control and tagging verification.',
        categories: [
            {
                label: 'AI Product Content Writer',
                icon: Sparkles,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'New tabbed Product Overview — "Base Info" tab for raw product brain dump with voice-to-text, "Content" tab for polished marketing content, "Agent Writer" tab for AI-generated structured messaging',
                    '"Rewrite with AI" modal (90% viewport) — select an agent persona (Default, Technical, Executive, Casual) and generate structured marketing content from raw product information',
                    'AI generates 18-section framework output: value proposition, target audience, problem statement, functional/business/emotional benefits, differentiation, headlines, subheadlines, CTAs, and marketing page copy',
                    'Built on the AI Product Value Messaging Framework — converts features into outcomes, outcomes into business value, and business value into emotionally relevant benefits',
                    'Edit & Commit panel — review and edit AI output, then commit Short Description, Detailed Content, and Features back to the product in one click',
                    'Agent Writer tab persists AI output — view, rewrite, or commit previous AI generations without regenerating',
                    'Base Info field included in version snapshots — full version control for raw product information',
                ],
            },
            {
                label: 'Signature Template Improvements',
                icon: Zap,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Signature merge variables now resolve when inserting into emails — {{SenderName}}, {{SenderTitle}}, {{SenderEmail}}, {{SenderPhoto}}, and {{CompanyLogo}} are replaced with actual user data',
                    'Profile photos now render correctly in signature previews — fixed S3 presigned URL resolution for the admin user preview',
                    'User preview dropdown is now searchable with alphabetical sorting — type to filter by name or email',
                    'Default signature auto-resolves merge variables when auto-loaded in Inbox compose',
                    'Signature merge variables also resolve in Email Editor Modal and Sequence Editor',
                ],
            },
            {
                label: 'Forms & Tagging',
                icon: Zap,
                color: 'text-green-500 bg-green-50',
                items: [
                    'Auto-apply tags on form submission verified working — tags are applied to both new and existing contacts',
                    'New "Open in new tab" checkbox on redirect URL — control whether form redirect opens in a new tab (default) or same window',
                    'Backend returns redirect_new_tab setting in both public form data and submission response',
                ],
            },
            {
                label: 'Admin & UX',
                icon: Zap,
                color: 'text-stone-500 bg-stone-50',
                items: [
                    'Admin /me endpoint now resolves S3 presigned URLs for profile photos and company logos',
                    'Organization logo URL passed to user context for signature {{CompanyLogo}} resolution',
                    'Admin users endpoint resolves profile photo and company logo S3 keys to presigned URLs',
                ],
            },
        ],
    },
    {
        version: 'v2.20.0',
        date: 'April 7, 2026',
        title: 'Agentic Personalization — AI That Writes Like You',
        summary: 'Major release introducing Agentic Personalization — a new campaign capability where AI writes unique, personalized emails for every contact in the sender\'s authentic voice, adapted to the receiver\'s DISC profile and communication history. Includes a full human-in-the-loop review queue, AI context files on contacts and companies, quality scoring, and a feedback learning loop.',
        categories: [
            {
                label: 'Agentic Personalization',
                icon: Sparkles,
                color: 'text-violet-500 bg-violet-50',
                items: [
                    'New "AI Personalized Email" campaign step — the AI writes a unique email for every enrolled contact using the sender\'s voice profile, receiver\'s DISC profile, company context, and full communication history',
                    'AI assembles 4 context files per email: Sender Voice MD, Contact MD, Account/Company MD, and Communication Pair MD — giving the AI deep relationship context',
                    'Emails are adapted to each contact\'s DISC communication style (pace, detail level, tone, decision style) without explicitly mentioning DISC',
                    'Step configuration: set the objective, tone override, email length (short/medium/long), and call-to-action type (reply, meeting, link click, custom)',
                    'Quality scoring on 5 dimensions: Voice Match, DISC Alignment, Relevance, Clarity, and CTA Strength — scored automatically at generation time',
                    'Feedback learning loop — rejection reasons and edit patterns are fed back into future AI prompts so quality improves over time',
                ],
            },
            {
                label: 'AI Review Queue (Human-in-the-Loop)',
                icon: ShieldCheck,
                color: 'text-green-500 bg-green-50',
                items: [
                    'New "AI Reviews" page under AI Tools — every AI-generated email requires human approval before sending',
                    'Review cards show contact name, DISC badge, campaign name, quality score, and email preview at a glance',
                    'Detail panel with full email preview, AI coaching notes, and quality score breakdown',
                    'One-click Approve & Send — approve the draft and send immediately, campaign continues to next step',
                    'Inline Edit mode — edit the subject and body directly, then send the edited version',
                    'Reject with feedback — your feedback trains the AI to write better drafts for this org',
                    'Regenerate with guidance — tell the AI what to change ("make it shorter", "more casual tone") and get a new draft instantly',
                    'Sidebar badge shows pending review count, refreshes every 60 seconds',
                    'Status filters: Pending, Approved, Edited, Rejected, All',
                ],
            },
            {
                label: 'AI Context Files',
                icon: FileText,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'New "AI Context" tab on every Contact detail page — view the AI\'s profile of each contact including DISC, deals, activities, and communication preferences',
                    'New "AI Context" tab on every Company detail page — company overview, key people, deal pipeline, relationship history',
                    'Communication Pair MD — summarizes all past interactions between a specific sender and receiver, including tone, topics, and buying signals',
                    'Auto-generation — context files are created automatically on first access and refreshed when stale (>24 hours)',
                    'Manual editing — override any AI-generated context with your own notes and insights',
                    'Version history — every edit and regeneration is versioned, with the ability to view and compare past versions',
                    'Regenerate on demand — force a fresh generation anytime with one click',
                ],
            },
            {
                label: 'Dashboard & Analytics',
                icon: Brain,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Review queue metrics endpoint — pending count, approval rate, edit rate, rejection rate, average quality score over 30 days',
                    'Quality trend tracking — daily average quality scores for spotting improvement over time',
                    'Notification system — get notified when AI drafts are ready for your review',
                    'Agentic Personalization feature gating — available on Pro and Enterprise plans, manageable via Platform Admin feature overrides',
                ],
            },
        ],
    },
    {
        version: 'v2.19.0',
        date: 'April 7, 2026',
        title: 'Website & Funnel Builder, Campaign Branching, Zero-Downtime Deploys',
        summary: 'Major release introducing a full website and funnel builder with 16 block types, AI page generation, A/B testing, and custom domains. Campaign workflows now support branching on form completion dates and tag dates. Infrastructure upgraded to zero-downtime deployments.',
        categories: [
            {
                label: 'Website & Funnel Builder',
                icon: Sparkles,
                color: 'text-purple-500 bg-purple-50',
                items: [
                    'New Sites module — build landing pages, microsites, and multi-step sales funnels from the sidebar',
                    '16 block types: Hero, Text, Image, Features, CTA, Footer, Form, Testimonials, Pricing, Booking, FAQ, Stats, Logo Cloud, Video, Divider, Gallery — each with multiple variants',
                    'Visual drag-and-drop editor with three-panel layout: block palette (left), canvas (center), properties (right)',
                    'Click-to-add blocks — click any block in the palette to instantly add it to your page',
                    'Drop zones expand during drag for easier targeting between blocks',
                    'Canvas-level drop fallback — dropping anywhere on the canvas adds the block',
                    'AI Builder panel — generate entire pages, add sections, improve copy, or run SEO checks using AI',
                    'Funnel mode with horizontal step bar, drag-to-reorder steps, step types, and conversion goals',
                    'A/B testing — create page variants, set traffic split percentages, compare conversion rates',
                    'Custom domains with provider-specific DNS guides (Cloudflare, GoDaddy, Namecheap, Route 53, Google Domains)',
                    'Global styles panel — brand colors, fonts, favicon, and SEO metadata',
                    'Static HTML publishing with CDN delivery',
                    '8 pre-built templates seeded on first startup',
                    'Template picker with category tabs and search when creating new sites',
                ],
            },
            {
                label: 'Campaign Enhancements',
                icon: Zap,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Branch on Form Completion Date — If/Else steps can now evaluate when a contact last completed a form',
                    'Branch on Tag Date — branch based on when a tag was added (not just whether it exists)',
                    'Tag history tracking — every tag addition/removal is timestamped on the contact record',
                    'New condition fields: Last Tagged Date, Form Completion Date, Last Submission Date, Form Name, Event Tag Date',
                    'Friendly date operator labels: "more than N days ago", "less than N days ago", "has a date", "has no date"',
                    'Form submissions now stamp last_form_completed_at on the contact for condition engine access',
                    'New "Form" condition group in the condition builder with submission date and form name fields',
                ],
            },
            {
                label: 'Infrastructure',
                icon: Shield,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Zero-downtime deployments — ASG instance refresh now scales to 2 instances during deploy, waits for new instance to pass health checks, then drains and terminates the old one',
                    'ALB health check interval reduced from 30s to 10s for faster new-instance detection',
                    'Deploy script updated with MaxHealthyPercentage: 200, InstanceWarmup: 120s, SkipMatching: true',
                    'Updated reconnecting screen — shows "Updating with new features" message with auto-retry (polls /api/health every 5s)',
                    'Shared ReconnectingScreen component replaces duplicated code in ProtectedRoute and AdminRoute',
                ],
            },
            {
                label: 'UX Improvements',
                icon: Rocket,
                color: 'text-green-500 bg-green-50',
                items: [
                    'Import wizard skip reasons — when reimporting a CSV, the results now show exactly why rows were skipped (duplicate email, missing required fields) instead of a generic "skipped" count',
                    'Custom domain panel — expandable provider-specific DNS guides with step-by-step instructions',
                    'Root domain detection — warns users to use a subdomain when a root domain is entered',
                    'Sites feature added to Super Admin feature overrides panel',
                ],
            },
            {
                label: 'Help & Documentation',
                icon: Rocket,
                color: 'text-cyan-500 bg-cyan-50',
                items: [
                    'New help guide: Campaign Branching & Conditions — covers If/Else branches, form completion date branching, tag date branching, A/B splits, and all available condition fields',
                    'New help guide: Website Builder — creating sites, adding blocks, AI builder, preview, global styles, publishing',
                    'New help guide: Funnel Builder — creating funnels, managing steps, conversion goals, A/B testing, analytics',
                    'New help guide: Custom Domains for Sites — DNS setup with provider-specific instructions',
                    'New "Websites & Funnels" group in the help guide sidebar',
                ],
            },
        ],
    },
    {
        version: 'v10.6.1',
        date: 'April 6, 2026',
        title: 'Affiliate Tracking Module — AI Agility Check Integration',
        summary: 'Complete affiliate tracking system built from scratch today. Designed with a product-agnostic architecture so any future affiliate product can be added through configuration alone. The first integration is AI Agility Check (aiagilitycheck.com), an AI readiness diagnostic that scores organizations across Strategy, People, Technology, and Execution pillars. The module spans 12 files (6 new, 6 modified) with ~1,600 lines of code across backend and frontend.',
        categories: [
            {
                label: 'New Features',
                icon: Sparkles,
                color: 'text-purple-500 bg-purple-50',
                items: [
                    'New Affiliates page accessible from the Insights section of the sidebar — a full-featured hub for managing affiliate partnerships and tracking referral performance',
                    'Overview Tab — KPI dashboard showing total referrals, completed assessments, registered contacts, and conversion rate. Includes per-product breakdown cards with logo/color branding, a top affiliates leaderboard table (admin view), and a recent referrals feed with status badges and quick-view links to contacts',
                    'Referrals Tab — filterable data table listing every referral with name, email, company, status badge, S/P/T/E assessment scores in dedicated columns, total score, referring affiliate name, and a click-through to the contact record. Filters for status (registered/completed) and product',
                    'My Links Tab — each user can view their personal affiliate referral URLs per product, copy them to clipboard with one click, and edit their referral codes. Shows product branding (logo, color) and generates the full referral URL from a configurable template',
                    'Products Tab (admin-only) — CRUD interface for affiliate product integrations. Each product card shows name, slug, active status, referral count, and completed count. Full configuration dialog with fields for name, slug, API base URL, API key, webhook secret, referral URL template, logo URL, and brand color picker. Displays the webhook URL for easy setup on the partner side',
                    'Sync button on each product card — pulls data from the external REST API to reconcile referrals and backfill any missed webhook events',
                    'Refresh button in the page header — invalidates all affiliate queries (dashboard, referrals, products, links) for an instant data reload',
                    'Webhook receiver at /api/webhooks/aiagilitycheck — processes 4 event types in real time: user.registered (creates contact + referral), assessment.completed (updates scores on referral + contact), affiliate.registered (auto-links affiliate code to CRM user by email match), and affiliate.referral (creates referral + contact from affiliate-sourced signups)',
                    'Assessment scores on Contact Detail — when a contact has AI Agility Check data, a dedicated card appears in the About tab showing the overall score out of 240, a maturity level badge (Beginning/Emerging/Developing/Advanced), completion date, and four color-coded progress bars for Strategy, People, Technology, and Execution pillars (each scored out of 60)',
                    'Automatic contact creation — when a referral webhook arrives, the system matches the email to an existing contact or creates a new one with full enrichment: first/last name (parsed from full name), email, job title, company, industry, country, employee count, and an "affiliate-referral" tag. The contact is assigned to the referring affiliate as owner',
                    'Automatic follow-up tasks — every new referral creates a high-priority task assigned to the affiliate user with a 24-hour deadline. Task title includes the contact name and product, description provides context and a prompt to reach out. Tasks appear on the affiliate\'s Tasks page and Inbox tasks tab, tagged with source "affiliate_referral"',
                    'Default attribution — when a contact registers on AI Agility Check without any affiliate code (direct traffic), the referral is automatically attributed to the organization\'s default contact so no lead goes unowned. The referral record shows "(direct)" as the affiliate code',
                    'Affiliate-to-CRM user mapping — each CRM user can have affiliate codes stored per product (e.g., affiliate_codes.aiagilitycheck = "fdn40b7i"). When webhooks arrive with a referral code, the system looks up which CRM user owns that code and attributes the referral accordingly',
                    'Plan-based feature gating — the Affiliates feature is available on Starter, Pro, and Enterprise plans. Free plan users see the sidebar item with a lock icon. Enforced on both frontend (PlanContext) and backend (enforce_feature)',
                ],
            },
            {
                label: 'Infrastructure',
                icon: Shield,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Product-agnostic data architecture — affiliate_products collection stores per-product configuration (API credentials, webhook secrets, URL templates, branding). affiliate_referrals collection stores individual referral records linked to products, contacts, and affiliate users. Adding a future affiliate product (e.g., a different assessment tool) requires only a new document in affiliate_products, zero code changes',
                    'HMAC-SHA256 webhook signature verification — each product has its own webhook secret. Raw request body is verified against the X-Signature or X-Hub-Signature-256 header before processing. Invalid signatures are logged and skipped per-product (multi-org safe)',
                    'Async webhook processing — returns HTTP 200 immediately, then processes events via asyncio.create_task() to avoid timeout issues with external webhook callers',
                    'REST API client for AI Agility Check — thin httpx wrapper covering all 8 CRM API endpoints: get_users, get_user, get_assessments, get_assessment, get_affiliates, get_affiliate, get_affiliate_referrals, and get_stats. Used by the Sync feature to pull and reconcile data',
                    'MongoDB compound indexes — unique index on (org_id, slug) for products, compound indexes on (org_id, product_id, email), (org_id, affiliate_user_id), and (org_id, created_at) for referrals. Ensures fast queries at scale',
                    'React Query hooks layer — 9 custom hooks (useAffiliateProducts, useAffiliateReferrals, useAffiliateDashboard, useMyAffiliateLinks, useCreateProduct, useUpdateProduct, useDeleteProduct, useSyncProduct, useUpdateMyLinks) with appropriate stale times and query key invalidation on mutations',
                    'Backend route structure follows existing patterns — local current_user and require_org_admin dependency functions, serialize_doc helper, consistent error handling with HTTPException',
                ],
            },
            {
                label: 'Improvements',
                icon: Zap,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Contact model extended with assessments field — a flexible dictionary keyed by product slug (e.g., assessments.aiagilitycheck) that stores registration date, completion date, status, pillar scores, and total score. Designed to support multiple assessment products per contact',
                    'Dashboard endpoint uses MongoDB aggregation pipeline for top affiliates ranking — groups by affiliate_user_id, counts total and completed referrals, sorts by volume, and enriches with user names',
                    'Referral list endpoint enriches each record with the affiliate user\'s display name for the admin view, while non-admin users are scoped to only see their own referrals',
                    'All referral records store raw_webhook_data for debugging and audit trail purposes',
                ],
            },
        ],
    },
    {
        version: 'v10.5.0',
        date: 'April 5, 2026',
        title: 'Tasks, Waffle Menu, WhatsApp & Zero-Downtime Deploys',
        summary: 'Four major features built and deployed in a single day. A complete task management system with AI-powered suggestions, a Gmail-style app launcher for org-wide navigation, WhatsApp Business messaging integration, and a video testimonial collection agent. Also includes a critical fix for users being logged out during backend deployments, plus a full sidebar redesign with collapsible groups.',
        categories: [
            {
                label: 'New Features',
                icon: Sparkles,
                color: 'text-purple-500 bg-purple-50',
                items: [
                    'Tasks Module (Phase 1) — brand new /tasks page inspired by Todoist. Features a quick-add input bar at the top for instant task creation, with tasks automatically organized into date-grouped sections: Overdue (red highlight), Today, Tomorrow, This Week, and Later. Each task shows title, due date, priority badge, and linked contact/deal. Bulk actions allow completing or deleting multiple tasks at once. The sidebar shows a live badge count of overdue tasks so nothing falls through the cracks',
                    'AI Task Suggestions (Phase 2) — a new POST /api/ai/suggest-tasks endpoint that feeds your CRM data (recent activities, pipeline state, stale contacts, upcoming meetings) into GPT-4o to generate 3-5 prioritized, actionable task suggestions. Each suggestion includes a title, description, priority, due date, and linked contact. Suggestions appear in a dedicated panel with Accept (creates the task), Dismiss, and Accept All buttons',
                    'AI Suggestion Panel on Tasks page — click the sparkles icon in the Tasks header to open the AI suggestion panel. Hit "Generate Suggestions" and GPT-4o analyzes your pipeline, activity gaps, and overdue items to recommend specific follow-ups. One-click accept turns a suggestion into a real task',
                    'AI Suggestion Panel on Contact Detail — a context-aware "AI Tasks" button in the quick-log bar on each contact. Generates task suggestions specific to that contact based on their deal stage, last interaction date, DISC profile, and communication history',
                    'Inbox Tasks Tab — a new "Tasks" tab in the Inbox page showing a focused view of today\'s tasks and overdue items. Includes a quick-add input for creating tasks without leaving the inbox, and a "View All Tasks" link to the full Tasks page. Designed for the morning workflow: check emails, check tasks, start your day',
                    'Post-Interaction Follow-Up Prompt — after logging a call, meeting, or note on a Contact Detail page, a HubSpot-style prompt appears asking if you want to create a follow-up task. Reduces the chance of forgetting next steps after every interaction',
                    'Waffle Menu (App Launcher) — a Gmail-style 9-square grid icon in the header bar, between notifications and your avatar. Clicking it opens a dropdown with a 3-column CSS grid of app icons. Each app shows a logo image (with a colored letter-initial fallback if the image fails to load) and opens in a new tab. The menu auto-hides entirely when no apps are configured, so it never shows an empty grid',
                    'Admin App Launcher Configuration — new "App Launcher" tab in the Admin page. Org admins can add external app links with a name, URL, icon URL, and brand color. A table view with up/down arrow buttons for reordering, plus edit and delete. Live preview grid shows exactly what users will see in the waffle menu. Data stored on Organization.settings.app_links and served through the /auth/me endpoint — no new API routes needed',
                    'WhatsApp Business Integration (Phase 1 — Foundation) — dual-approach architecture supporting both self-managed BYO accounts (via Meta Embedded Signup, fully implemented) and platform-managed shared numbers (stubbed for future). Full backend: Pydantic models for WhatsApp configs and messages, credential resolver using AWS SSM Parameter Store, Meta Graph API wrapper for sending text/template/media messages, webhook endpoints for message delivery and status callbacks',
                    'WhatsApp Chat UI in Inbox — a new WhatsApp tab in the Inbox page with a conversation list and chat interface. Real-time message display with aggressive React Query polling. Send text messages directly from the CRM to any WhatsApp contact. Messages are linked to CRM contacts and appear in their communication timeline',
                    'WhatsApp in Campaigns — WhatsApp message as a new campaign step type in the journey builder. Send template messages as part of automated campaign workflows. WhatsApp events (message delivered, read) available as campaign triggers',
                    'Video Testimonial Agent — complete testimonial collection system built from scratch. Multi-page builder where you configure the testimonial request (questions, branding, settings). Generates a public submission URL that clients can visit to record video testimonials directly in their browser using the MediaRecorder API with a 3-2-1 countdown. Voice-to-text transcription of recordings, AI-powered script improvement suggestions, star rating collection, and a full approval workflow (pending → approved → published)',
                    'Testimonial Embed Codes & Wall — approved testimonials can be displayed publicly via embed codes (iframe snippet) or a dedicated testimonial wall page. Wall shows testimonials in a responsive grid with star ratings, company names, and video playback',
                    'Content Library — new centralized repository at /content-library for storing reusable content blocks (email templates, proposal snippets, common responses). WYSIWYG TipTap editor for creating and editing content. ContentPicker component integrated across all rich text editors in the app so users can insert saved content with a few clicks',
                    'Sidebar Redesign — completely reorganized navigation from a flat list into collapsible groups: CRM (Companies, Contacts, Pipeline, Deal Rooms, Products, Deal Registration), Outreach (Proposals, Email Sequences, Campaigns, Forms, Booking Links, Content Library, Testimonials), AI Tools (AI Assistant, AI Heartbeat), Insights (Activities, Reports, List Management), and Support (Feature Requests, Release Notes). Groups remember their expanded/collapsed state across sessions via localStorage',
                ],
            },
            {
                label: 'Bug Fixes',
                icon: Bug,
                color: 'text-red-500 bg-red-50',
                items: [
                    'Fixed users getting logged out during backend deployments — the root cause was the ASG instance refresh with MinHealthyPercentage:0, which terminated the old instance before the new one was healthy. During the ~60 second gap, /api/auth/me returned 502, and checkAuth() treated it as an auth failure, clearing the token and redirecting to login. The fix has two parts: (1) AuthContext.checkAuth() now retries 3 times with 3-second delays on transient errors (502, 503, network timeouts) — only clears the session on definitive 401/403 responses; (2) if all retries fail, the token is preserved and the user stays on the page rather than being kicked to login',
                    'Added "Reconnecting..." UI screen — when a stored token exists but the user object is null (backend temporarily unavailable), both ProtectedRoute and AdminRoute now show a friendly reconnection screen with a warning icon, explanatory message, and a "Retry Now" button instead of silently redirecting to the login page. This covers the edge case where the backend comes back but React hasn\'t re-checked auth yet',
                ],
            },
            {
                label: 'Infrastructure',
                icon: Shield,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Zero-downtime rolling deploys — ASG instance refresh MinHealthyPercentage changed from 0 to 100. This means AWS must launch a new EC2 instance and confirm it passes health checks before terminating the old one. Users experience zero interruption during backend deployments. Updated in CLAUDE.md deploy instructions as the permanent standard',
                    'WhatsApp credential resolver — secure token management using AWS SSM Parameter Store. WhatsApp Business API tokens, phone number IDs, and business account IDs stored as encrypted SSM parameters under /nurturecrm/whatsapp/. Credential resolver fetches and caches tokens with automatic refresh',
                    'Meta Graph API wrapper — full HTTP client for WhatsApp Cloud API (v18.0) covering send_text_message, send_template_message, send_media_message, mark_as_read, get_media_url, and download_media. Webhook verification endpoint for Meta\'s hub.challenge handshake. All API calls include proper error handling and logging',
                    '6 new task API endpoints — GET /api/activities/tasks (list with date grouping), POST /api/activities/tasks (quick create), PUT /api/activities/tasks/{id}/complete, PUT /api/activities/tasks/{id}/reopen, GET /api/activities/tasks/counts (overdue + today counts for sidebar badge), DELETE /api/activities/tasks/{id}. Activity model extended with reminder_at (ISO date for future notification scheduling) and source field (manual, ai_suggested, follow_up, campaign, affiliate_referral) for tracking how tasks were created',
                    'AI suggest-tasks endpoint — POST /api/ai/suggest-tasks accepts optional context_type and context_id parameters. Fetches relevant CRM data (open deals, recent activities, stale contacts, upcoming calendar events), builds a structured prompt, and calls GPT-4o to generate JSON suggestions. Each suggestion includes title, description, priority, due_date, contact_id, and deal_id. Supports both global suggestions (pipeline-wide) and contact-scoped suggestions',
                    'Feature gating for WhatsApp — added "whatsapp" to plan tier feature flags (off for free, on for starter and above). Frontend checks via PlanContext, backend enforces via enforce_feature() on all WhatsApp endpoints',
                ],
            },
        ],
    },
    {
        version: 'v10.1.5',
        date: 'April 4, 2026',
        title: 'Stability & Reliability Release',
        summary: 'Major stability improvements across the entire application — fixed critical crashes on 16 pages, resolved API response handling, and hardened the backend.',
        categories: [
            {
                label: 'Bug Fixes',
                icon: Bug,
                color: 'text-red-500 bg-red-50',
                items: [
                    'Fixed `.map() is not a function` crashes across 16 pages (Companies, Contacts, Pipeline, Contact Detail, Account Detail, Deal Rooms, Inbox, Campaigns, and more)',
                    'Resolved 500 Internal Server Error on Companies page caused by datetime type mismatch in connection strength computation',
                    'Fixed CORS configuration — moved middleware to correct position so error responses include proper headers',
                    'Fixed Contact Detail showing "Contact not found" when deals API response format changed',
                    'Fixed organization settings overwrite — connecting Exa no longer wipes Apify key',
                    'Fixed empty Companies/Contacts tables caused by owner filter defaulting to current user',
                ],
            },
            {
                label: 'Improvements',
                icon: Zap,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Standardized API response handling across all pages with safe array extraction pattern',
                    'Inbox now uses two-phase loading (fast local data first, then Gmail sync)',
                    'Optimized communication count query with MongoDB aggregation pipeline',
                    'Added `_ensure_dt()` helper for robust datetime handling in backend',
                ],
            },
        ],
    },
    {
        version: 'v10.1.0',
        date: 'April 3, 2026',
        title: 'AI Campaigns, Email Upgrades & Contact Enhancements',
        summary: 'Massive feature release — AI-assisted campaign builder, full email composition upgrade with CC/BCC and tracking, enriched contact records, meeting scheduling, and voice-to-text throughout.',
        categories: [
            {
                label: 'New Features',
                icon: Sparkles,
                color: 'text-purple-500 bg-purple-50',
                items: [
                    'AI-Assisted Campaign Builder — describe your campaign in natural language and AI builds the workflow on the canvas',
                    'Campaign AI Chat Panel — iterative refinement of campaign workflows through conversation',
                    'Voice-to-Text (VoiceButton) — dictate campaign descriptions, notes, and emails using Web Speech API',
                    'Email Editor Modal — full TipTap rich text editor with merge variables, AI writer, and signature insertion',
                    'Contact Email Typeahead — search CRM contacts for To/CC/BCC fields',
                    'CC/BCC support in email composition',
                    'Email open & click tracking with invisible pixel and link wrapping',
                    'Communications History tab on Contact Detail — unified timeline of all interactions',
                    'Quick Log Buttons — one-click Note, Call, Task, and Meeting logging from contact records',
                    'Meeting Scheduler — schedule meetings with attendees, Google Meet/Zoom, and calendar integration',
                    'AI Email Signature Generator — generate branded HTML signatures from Admin panel',
                    'Auto-create default signature for new users on registration',
                ],
            },
            {
                label: 'Improvements',
                icon: Zap,
                color: 'text-amber-500 bg-amber-50',
                items: [
                    'Contacts page fully rewritten from card grid to DataTable with ViewSelector and column management',
                    'Campaign create modal now has Manual/AI-Assisted toggle',
                    'Email steps in campaign builder now open full editor modal instead of inline textarea',
                    'Contact Detail upgraded with tabbed layout for About, Communications, Activities, Deals, and Custom Fields',
                ],
            },
        ],
    },
    {
        version: 'v10.0.0',
        date: 'April 1, 2026',
        title: 'Platform Foundation & AWS Migration',
        summary: 'Complete infrastructure migration to AWS with new enrichment pipeline, campaign system, and 41 automated Playwright tests.',
        categories: [
            {
                label: 'Infrastructure',
                icon: Shield,
                color: 'text-blue-500 bg-blue-50',
                items: [
                    'Migrated from Emergent.sh to AWS (EC2 ASG + ALB, S3 + CloudFront, ECR, SSM)',
                    'Centralized LLM service with OpenAI SDK and usage tracking (tokens, cost per call)',
                    '4-source enrichment pipeline: website scraping → GPT-4o → Exa AI Search → LinkedIn URL finder',
                    'Terraform infrastructure-as-code for all AWS resources',
                    '41 automated Playwright UAT tests across 15 test suites',
                ],
            },
            {
                label: 'New Features',
                icon: Sparkles,
                color: 'text-purple-500 bg-purple-50',
                items: [
                    'Campaign journey system with visual ReactFlow builder',
                    'Booking links with public booking pages',
                    'Form builder with public form submissions',
                    'Deal rooms for collaborative deal management',
                    'AI Heartbeat — scheduled AI analysis of pipeline health',
                    'Feature requests portal for users',
                    'Onboarding flow for new users',
                ],
            },
        ],
    },
];

function ReleaseCard({ release, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-4 p-5 text-left hover:bg-stone-50 transition-colors"
            >
                <div className="flex-shrink-0 w-10 h-10 bg-stone-900 rounded-lg flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {release.version}
                        </span>
                        <span className="text-xs text-stone-400">•</span>
                        <span className="text-xs text-stone-500">{release.date}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-stone-800">{release.title}</h3>
                    <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{release.summary}</p>
                </div>
                {open ? <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />}
            </button>
            {open && (
                <div className="px-5 pb-5 space-y-4 border-t border-stone-100 pt-4">
                    <p className="text-sm text-stone-600">{release.summary}</p>
                    {release.categories.map((cat, i) => {
                        const Icon = cat.icon;
                        return (
                            <div key={i}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cat.color}`}>
                                        <Icon className="w-3 h-3" />
                                        {cat.label}
                                    </span>
                                </div>
                                <ul className="space-y-1.5 ml-1">
                                    {cat.items.map((item, j) => (
                                        <li key={j} className="flex items-start gap-2 text-sm text-stone-600">
                                            <span className="w-1.5 h-1.5 rounded-full bg-stone-300 mt-1.5 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function ReleaseNotes() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Release Notes
                        </h1>
                        <p className="text-sm text-stone-500 mt-0.5">What's new in NurtureCRM</p>
                    </div>
                </div>

                {/* Releases */}
                <div className="space-y-4">
                    {RELEASES.map((release, i) => (
                        <ReleaseCard key={release.version} release={release} defaultOpen={i === 0} />
                    ))}
                </div>

                {/* Footer */}
                <div className="text-center mt-8 mb-4">
                    <p className="text-xs text-stone-400">
                        Have a feature request?{' '}
                        <button onClick={() => navigate('/feature-requests')} className="text-stone-600 hover:text-stone-900 underline">
                            Let us know
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
